import { useState } from 'react';
import { Avatar, Button, Input, Popconfirm, Segmented, Space, Table, Tag, Tooltip, Typography, type TableColumnsType } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../auth/store';
import { QueryError } from '../../lib/QueryState';
import { LoadMore } from '../../lib/useCursorPages';
import { PortalPage } from './AgencyPortalPage';
import { usePortalListeners, usePortalSummary, type ListenerStatus, type PortalListenerRow } from './portalApi';
import { ReasonModal, useBlockActions, usePortalAction } from './portalActions';

type Filter = ListenerStatus | 'all';

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'pending', label: 'To review' },
  { value: 'interview_scheduled', label: 'Interview scheduled' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const STATUS_TAG: Record<ListenerStatus, { color: string; label: string }> = {
  pending: { color: 'processing', label: 'To review' },
  interview_scheduled: { color: 'purple', label: 'Interview' },
  approved: { color: 'success', label: 'Approved' },
  rejected: { color: 'error', label: 'Rejected' },
};

const CHECK_TAG: Record<string, { color: string; label: string }> = {
  none: { color: 'default', label: 'Not submitted' },
  uploaded: { color: 'processing', label: 'Waiting review' },
  pending_review: { color: 'processing', label: 'Waiting review' },
  verified: { color: 'success', label: 'Verified' },
  rejected: { color: 'error', label: 'Rejected' },
};

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');
const open = (s: ListenerStatus) => s === 'pending' || s === 'interview_scheduled';

/**
 * `/agency-portal/listeners`: the listeners who joined with this agency's code.
 * The agency has the final say on its own applicants — approving makes them
 * live with the tier they applied for (the Dude team's call-pricing table sets
 * what they charge and earn). ID documents and voice recordings stay with the
 * Dude team; the agency sees only whether they were checked.
 */
export function AgencyListenersPage() {
  const [params, setParams] = useSearchParams();
  const filter: Filter = FILTERS.find((f) => f.value === params.get('status'))?.value ?? 'pending';
  const [q, setQ] = useState('');
  const list = usePortalListeners({ status: filter === 'all' ? undefined : filter, q });
  const { can } = usePermissions();
  // Permission AND the Dude team's switch for this agency (the API checks both).
  const controls = usePortalSummary().data?.agency.controls;
  const canDecide = can('agency_portal.listeners.decide') && !!controls?.listeners;
  const canBlock = can('agency_portal.users.block') && !!controls?.users;

  const approve = usePortalAction<{ id: string }>((v) => `/listeners/${v.id}/approve`, () => ({}), 'Approved — she is now live', 'Could not approve');
  const reject = usePortalAction<{ id: string; reason: string }>(
    (v) => `/listeners/${v.id}/reject`,
    (v) => ({ reason: v.reason }),
    'Application rejected',
    'Could not reject',
  );
  const { block, unblock } = useBlockActions();
  const [rejecting, setRejecting] = useState<PortalListenerRow | null>(null);
  const [blocking, setBlocking] = useState<PortalListenerRow | null>(null);

  const columns: TableColumnsType<PortalListenerRow> = [
    {
      title: 'Listener',
      key: 'who',
      render: (_, r) => (
        <Space>
          <Avatar src={r.photoUrl ?? undefined} icon={<UserOutlined />} />
          <span>
            <Typography.Text strong>{r.displayName}</Typography.Text>
            <br />
            <Typography.Text type="secondary">@{r.handle}</Typography.Text>
            {r.blocked && <Tag color="error" style={{ marginLeft: 6 }}>Blocked</Tag>}
            {r.isSuspended && <Tag color="warning" style={{ marginLeft: 6 }}>Suspended</Tag>}
            {r.deleted && <Tag style={{ marginLeft: 6 }}>Deleted</Tag>}
          </span>
        </Space>
      ),
    },
    { title: 'Status', dataIndex: 'status', render: (s: ListenerStatus) => <Tag color={STATUS_TAG[s].color}>{STATUS_TAG[s].label}</Tag> },
    { title: 'Applied', dataIndex: 'createdAt', render: (v: string) => day(v) },
    { title: 'Languages', dataIndex: 'languages', render: (l: string[]) => l.join(', ') || '—' },
    { title: 'ID check', dataIndex: 'kyc', render: (k: string) => <Tag color={CHECK_TAG[k]?.color}>{CHECK_TAG[k]?.label ?? k}</Tag> },
    { title: 'Voice check', dataIndex: 'voice', render: (k: string) => <Tag color={CHECK_TAG[k]?.color}>{CHECK_TAG[k]?.label ?? k}</Tag> },
    {
      title: 'Calls',
      key: 'calls',
      align: 'right',
      render: (_, r) => (
        <Tooltip title={r.lastCallAt ? `Last call ${new Date(r.lastCallAt).toLocaleString()}` : undefined}>
          {r.calls.toLocaleString('en-IN')}
        </Tooltip>
      ),
    },
    {
      title: '',
      key: 'actions',
      render: (_, r) => (
        <Space wrap>
          {canDecide && open(r.status) && (
            <>
              <Popconfirm
                title={`Approve ${r.displayName}?`}
                description={
                  r.kyc !== 'verified'
                    ? 'Her ID is not verified yet. Approving makes her live and able to take calls now.'
                    : 'She goes live and can start taking calls.'
                }
                okText="Approve"
                onConfirm={() => approve.mutate({ id: r.id })}
              >
                <Button type="primary" size="small" loading={approve.isPending && approve.variables?.id === r.id}>
                  Approve
                </Button>
              </Popconfirm>
              <Button danger size="small" onClick={() => setRejecting(r)}>
                Reject
              </Button>
            </>
          )}
          {canBlock &&
            (r.blocked ? (
              <Popconfirm title={`Unblock @${r.handle}?`} onConfirm={() => unblock.mutate({ userId: r.userId })}>
                <Button size="small" loading={unblock.isPending && unblock.variables?.userId === r.userId}>
                  Unblock
                </Button>
              </Popconfirm>
            ) : (
              <Button size="small" onClick={() => setBlocking(r)}>
                Block
              </Button>
            ))}
        </Space>
      ),
    },
  ];

  return (
    <PortalPage title="Listeners">
      <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
        <Segmented<Filter>
          value={filter}
          onChange={(v) => setParams(v === 'pending' ? {} : { status: v }, { replace: true })}
          options={FILTERS}
        />
        <Input.Search allowClear placeholder="Search name or @handle" onSearch={setQ} style={{ width: 260 }} />
      </Space>
      {list.isError ? (
        <QueryError error={list.error} what="listeners" onRetry={list.refetch} />
      ) : (
        <>
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            columns={columns}
            dataSource={list.rows}
            pagination={false}
            scroll={{ x: 1000 }}
            expandable={{
              rowExpandable: (r) => !!r.bio || !!r.rejectionReason || r.expertiseTags.length > 0,
              expandedRowRender: (r) => (
                <Space direction="vertical" size={4}>
                  {r.bio && <Typography.Text>{r.bio}</Typography.Text>}
                  {r.expertiseTags.length > 0 && (
                    <span>
                      {r.expertiseTags.map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                    </span>
                  )}
                  {r.rejectionReason && <Typography.Text type="danger">Rejected: {r.rejectionReason}</Typography.Text>}
                </Space>
              ),
            }}
            locale={{ emptyText: filter === 'pending' ? 'No applications waiting for you' : 'No listeners here' }}
          />
          <LoadMore list={list} noun="listeners" />
        </>
      )}
      <ReasonModal
        open={!!rejecting}
        title={`Reject ${rejecting?.displayName ?? ''}?`}
        description="She is told her application was not approved. Write why — it is kept in the activity log."
        okText="Reject"
        loading={reject.isPending}
        onOk={(reason) => rejecting && reject.mutate({ id: rejecting.id, reason }, { onSuccess: () => setRejecting(null) })}
        onCancel={() => setRejecting(null)}
      />
      <ReasonModal
        open={!!blocking}
        title={`Block @${blocking?.handle ?? ''}?`}
        description="She is signed out, cannot use the app and stops receiving calls. You can unblock her account later, but an approved listener's call suspension is lifted by the Dude team."
        okText="Block"
        loading={block.isPending}
        onOk={(reason) => blocking && block.mutate({ userId: blocking.userId, reason }, { onSuccess: () => setBlocking(null) })}
        onCancel={() => setBlocking(null)}
      />
    </PortalPage>
  );
}
